import { useState, FormEvent, ChangeEvent } from 'react';
import type { CallRequestData } from '../types';
import { initiateCall } from '../utils/api';

interface CallRequestFormProps {
  onSuccess?: () => void;
}

export function CallRequestForm({ onSuccess }: CallRequestFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<CallRequestData>({
    user_name: '',
    user_surname: '',
    companyEmail: '',
    phoneNumber: '',
    callConsent: false,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError(null);
  };

  const validateForm = (): string | null => {
    if (!formData.user_name.trim() || formData.user_name.trim().length < 2) {
      return 'Please enter your first name (at least 2 characters)';
    }
    if (!formData.user_surname.trim() || formData.user_surname.trim().length < 2) {
      return 'Please enter your surname (at least 2 characters)';
    }
    if (!formData.companyEmail.trim() || !formData.companyEmail.includes('@')) {
      return 'Please enter a valid company email';
    }
    if (!formData.phoneNumber.trim()) {
      return 'Please enter your phone number';
    }
    if (!formData.callConsent) {
      return 'You must consent to receive a demo call to continue';
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await initiateCall(formData);
      
      if (result.success) {
        setIsSubmitted(true);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(result.message || 'Failed to initiate call. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setFormData({
      user_name: '',
      user_surname: '',
      companyEmail: '',
      phoneNumber: '',
      callConsent: false,
    });
    setError(null);
  };

  if (isSubmitted) {
    return (
      <div className="call-request-form success-state">
        <div className="success-badge">
          <svg className="icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span>Call in progress</span>
        </div>
        
        <h3>We are calling you now.</h3>
        
        <p className="success-message">
          Our AI assistant will greet you by name and continue the conversation instantly.
        </p>
        
        <button type="button" onClick={handleReset} className="reset-btn">
          Request another call
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="call-request-form">
      <div className="form-group">
        <label htmlFor="user_name">
          First name <span className="required">*</span>
        </label>
        <input
          type="text"
          id="user_name"
          name="user_name"
          value={formData.user_name}
          onChange={handleChange}
          placeholder="Jane"
          autoComplete="given-name"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="user_surname">
          Surname <span className="required">*</span>
        </label>
        <input
          type="text"
          id="user_surname"
          name="user_surname"
          value={formData.user_surname}
          onChange={handleChange}
          placeholder="Smith"
          autoComplete="family-name"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="companyEmail">
          Company email <span className="required">*</span>
        </label>
        <input
          type="email"
          id="companyEmail"
          name="companyEmail"
          value={formData.companyEmail}
          onChange={handleChange}
          placeholder="jane@company.com"
          autoComplete="email"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="phoneNumber">
          Phone number <span className="required">*</span>
        </label>
        <input
          type="tel"
          id="phoneNumber"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handleChange}
          placeholder="+1 415 555 2671"
          autoComplete="tel"
          required
        />
        <p className="field-hint">Include country code, for example +1 or +44.</p>
      </div>

      <div className="form-group consent-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="callConsent"
            checked={formData.callConsent}
            onChange={handleChange}
            required
          />
          <span>
            I agree to receive a live demo call from Propertly.io at the number provided. <span className="required">*</span>
          </span>
        </label>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="submit-btn"
      >
        {isSubmitting ? (
          <>
            <svg className="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
              <path d="M12 2a10 10 0 0 1 10 10" opacity="0.75"></path>
            </svg>
            Calling...
          </>
        ) : (
          'Call me now'
        )}
      </button>

      <p className="privacy-note">
        By submitting this form, you agree to our privacy policy and terms of service.
      </p>
    </form>
  );
}
